import { CHAT_TURN_HISTORY_MAX_MESSAGES } from "../constants/chatConstants";
import type {
  ChatMessage,
  ChatTurnHistoryMessage,
  ChatTurnSseEventName,
} from "../types/chat.types";

export interface ParsedSseEvent {
  event: ChatTurnSseEventName | string;
  data: unknown;
}

/** Map local thread to BFF history (user/assistant only). */
export function buildChatTurnHistory(messages: ChatMessage[]): ChatTurnHistoryMessage[] {
  const eligible = messages.filter(
    (m) => (m.role === "user" || m.role === "assistant") && !m.error && m.content.trim().length > 0
  );
  const capped =
    eligible.length > CHAT_TURN_HISTORY_MAX_MESSAGES
      ? eligible.slice(-CHAT_TURN_HISTORY_MAX_MESSAGES)
      : eligible;
  return capped.map((m) => ({
    role: m.role as "user" | "assistant",
    content: m.content,
  }));
}

/**
 * Parse one SSE event block (`event:` + `data:` lines). Returns null when incomplete.
 */
export function parseSseEventBlock(block: string): ParsedSseEvent | null {
  const lines = block.split(/\r?\n/).filter((line) => line.length > 0);
  if (lines.length === 0) return null;

  let eventName = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  const dataRaw = dataLines.join("\n");
  if (!dataRaw) {
    return { event: eventName, data: {} };
  }

  try {
    return { event: eventName, data: JSON.parse(dataRaw) as unknown };
  } catch {
    return { event: eventName, data: dataRaw };
  }
}

/** Incremental SSE parser over fetch ReadableStream chunks. */
export function createSseStreamParser(onEvent: (event: ParsedSseEvent) => void): {
  feed: (chunk: string) => void;
  flush: () => void;
} {
  let buffer = "";

  const dispatchBlocks = (text: string, final: boolean) => {
    buffer += text;
    const parts = buffer.split(/\n\n/);
    if (!final) {
      buffer = parts.pop() ?? "";
    } else {
      buffer = "";
    }
    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const parsed = parseSseEventBlock(trimmed);
      if (parsed) onEvent(parsed);
    }
    if (final && buffer.trim()) {
      const parsed = parseSseEventBlock(buffer.trim());
      if (parsed) onEvent(parsed);
      buffer = "";
    }
  };

  return {
    feed: (chunk: string) => dispatchBlocks(chunk, false),
    flush: () => dispatchBlocks("", true),
  };
}

function normaliseSuggestedPrompts(
  raw: unknown
): import("../types/chat.types").SuggestedPromptSummary[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const items = raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const promptType = (row.promptType ?? row.prompt_type) as string | undefined;
      const name = row.name as string | undefined;
      if (!promptType || !name) return null;
      return {
        promptType,
        name,
        description: (row.description as string | undefined) ?? undefined,
        setupReason: (row.setupReason ?? row.setup_reason) as string | undefined,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x != null);
  return items.length ? items : undefined;
}

export function normaliseTurnMetadata(
  raw: Record<string, unknown> | undefined,
  suggestedPromptsRaw?: unknown
): import("../types/chat.types").ChatMessageMetadata | undefined {
  const suggestedPrompts = normaliseSuggestedPrompts(suggestedPromptsRaw);
  if (!raw && !suggestedPrompts) return undefined;
  const tokenUsageRaw = raw?.token_usage as
    | { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number }
    | undefined;
  return {
    cached: raw?.cached as boolean | undefined,
    modelId: (raw?.model as string) ?? (raw?.modelId as string | undefined),
    estimatedCostUsd: raw?.estimated_cost_usd as number | undefined,
    responseTimeMs:
      typeof raw?.response_time === "number" ? (raw.response_time as number) * 1000 : undefined,
    tokenUsage: tokenUsageRaw
      ? {
          prompt: tokenUsageRaw.prompt_tokens ?? 0,
          completion: tokenUsageRaw.completion_tokens ?? 0,
          total: tokenUsageRaw.total_tokens ?? 0,
        }
      : undefined,
    suggestedPrompts,
  };
}
